import { groupBy, omit, pickBy } from 'lodash-es';
import {
  FILTER_OPERATOR_ENUMS,
  FILTER_RULE_ENUMS,
  REASONS_OF_REJECTION,
} from '@constants';
import { startOfDay, differenceInDays } from 'date-fns';
import { formatDate, sortArray } from '@utils';
import { URLS } from 'layouts/DrawerLayout/constants';
import { SELL_REQUESTS_PAGE_SIZE, SELL_REQUESTS_BOARD } from './constants';

export const getCleanedUrlParams = (params) =>
  pickBy(params, (item) => item !== null && item !== undefined && item !== '');

export const getUrl = (params) => {
  const urlParams = getCleanedUrlParams(params);
  const searchParams = new URLSearchParams(urlParams).toString();

  return `${URLS.sellRequests}?${searchParams}`;
};
export const getSellRequestsQueryParams = (payload = {}) => {
  if (!payload?.pageSize) {
    payload.pageSize = SELL_REQUESTS_PAGE_SIZE;
  }
  if (!payload?.page) {
    payload.page = '1';
  }

  if (!payload.order) {
    payload.order = 'desc';
    payload.orderBy = 'createdAt';
  }

  return getCleanedUrlParams(payload);
};

export const getSellRequestsPayload = (payload = {}) => {
  const fields =
    omit(payload, [
      'page',
      'pageSize',
      'order',
      'orderBy',
      'boardType',
      'query',
      'filters',
      'archived',
      'query',
    ]) || {};
  const { filters, archived } = payload;

  const value = [
    {
      field: 'archived_at',
      operator: 0,
      type: archived ? FILTER_RULE_ENUMS.noEmpty : FILTER_RULE_ENUMS.isEmpty,
    },
    ...Object.keys(fields).map((field) => {
      if (field === 'status') {
        const rawValue = payload[field]?.value ?? payload[field];
        const values = Array.isArray(rawValue)
          ? rawValue
          : String(rawValue).split(',');
        return {
          field,
          operator: FILTER_OPERATOR_ENUMS['='],
          type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
          value: values.map(Number),
        };
      }
      if (field === 'assignee') {
        const assigneeValue = payload[field]?.value || payload[field];
        return {
          field,
          operator: FILTER_OPERATOR_ENUMS['='],
          type:
            assigneeValue === 'none'
              ? FILTER_RULE_ENUMS.isEmpty
              : FILTER_RULE_ENUMS.ComparableRuleType,
          value: assigneeValue === 'none' ? '' : assigneeValue,
        };
      }
      if (typeof payload[field] === 'string' && payload[field].includes(',')) {
        return {
          field,
          operator: FILTER_OPERATOR_ENUMS['='],
          type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
          value: Array.isArray(payload[field])
            ? payload[field]
            : payload[field]?.split(','),
        };
      }
      return {
        field,
        operator: FILTER_OPERATOR_ENUMS['='],
        type: payload[field]?.type || FILTER_RULE_ENUMS.ComparableRuleType,
        value: payload[field]?.value || payload[field],
      };
    }),
    ...(filters?.value || []),
  ];

  return {
    type: FILTER_RULE_ENUMS.AndRuleType,
    field: null,
    operator: null,
    value,
  };
};

const computeOfferRequestColor = (offerRequestDate) => {
  if (offerRequestDate) {
    const given = new Date(offerRequestDate);
    const current = startOfDay(new Date());
    const dayDiff = differenceInDays(current, given);
    if (dayDiff >= 35) {
      return '#FFA366';
    }
    return '#03AA46';
  }
  if (!offerRequestDate) {
    return '#FF0000';
  }
};

export const normalizeSellRequestsTasks = (data = [], assignees = []) => {
  if (!data.length > 0) {
    return {};
  }
  let totalFinalPrice = 0;

  const assigneesMap = assignees.reduce((acc, cur) => {
    acc[cur.email] = {
      ...cur,
      shortName: `${cur.firstName?.[0]}${cur.lastName?.[0]}`.toUpperCase(),
    };
    return acc;
  }, {});

  const tasks = data.map((item) => {
    const assignee = assigneesMap[item.assignee] || {};
    if (item?.customFields?.finalPrice?.number) {
      totalFinalPrice += Number(item?.customFields?.finalPrice?.number);
    }
    return {
      ...item,
      href: `${URLS.sellRequests}/${item?.id}`,
      status: item.status,
      assignee,
      description: item?.name,
      createdAt: item?.createdAt
        ? formatDate(item?.createdAt, 'dd/MM/yyyy')
        : '-',
      date: item?.createdAt ? formatDate(item?.createdAt, 'dd/MM/yyyy') : '-',
      userId: item?.customFields?.userId,
      customFields: {
        ...item.customFields,
        finalPrice: item?.customFields?.finalPrice?.number
          ? new Intl.NumberFormat('el-GR', {
              style: 'currency',
              currency: 'EUR',
            }).format(item?.customFields?.finalPrice?.number)
          : '-',
        offerSentDate: item?.customFields?.offerSentDate
          ? formatDate(item?.customFields?.offerSentDate, 'dd/MM/yyyy')
          : '-',
        offerRequestDateColor: computeOfferRequestColor(
          item?.customFields?.offerRequestDate
        ),
        approvalStatus: item?.customFields?.approvalStatus || '-',
        loan: item?.customFields?.loan,
        loanContractId: item?.customFields?.loanContractId || '-',
        vehiclePlate: item?.customFields?.vehiclePlate || '-',
        appointmentDate: item?.customFields?.appointmentDate
          ? formatDate(item?.customFields?.appointmentDate, 'yyyy/MM/dd HH:mm')
          : '-',
        billedAt: item?.customFields?.billedAt
          ? formatDate(item?.customFields?.billedAt, 'dd/MM/yyyy')
          : '-',
      },
      name: item?.name || '',
    };
  });

  return {
    tasks,
    totalFinalPrice: new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
    }).format(totalFinalPrice),
  };
};

export const getColumns = (tasks = [], board = {}) => {
  const order = board.statusOrder;
  const groupedByStatus = groupBy(tasks, 'status');

  const columns = order.reduce((acc, cur) => {
    acc.push({
      id: cur,
      title: board?.statuses?.[cur]?.label || cur,
      cards: groupedByStatus[cur] || [],
      styles: board?.statuses?.[cur]?.styles,
      value: true,
      label: board?.statuses?.[cur]?.label || cur,
      key: cur,
    });

    return acc;
  }, []);

  return columns;
};

export const normalizeVehicleData = (data = {}) => {
  const properties = [];
  if (data.sku?.engine?.fuelType) {
    properties.push(data.sku.engine.fuelType);
  }
  if (data.sku?.transmission) {
    properties.push(data.sku.transmission);
  }
  if (data.properties?.color) {
    properties.push(data.properties.color);
  }
  if (data.sku?.year) {
    properties.push(data.sku.year);
  }

  return {
    internalId: data.vehicleInternalId,
    id: data.id,
    title: [data.sku?.brand, data.sku?.model, data.sku?.edition]
      .filter(Boolean)
      .join(' '),
    plate: data.plate,
    image: data?.images?.[0]?.url,
    properties,
    vin: data.vin,
    procurement: data.procurement,
  };
};

const handleAmountInstallmentsItem = (newAmountItem) => {
  if (newAmountItem.Key === 'amount') {
    return {
      ...newAmountItem,
      Value: newAmountItem.Value.map((amountValueItem) => ({
        ...amountValueItem,
        Value:
          amountValueItem.Key === 'number'
            ? amountValueItem.Value / 100
            : amountValueItem.Value,
      })),
    };
  }
  return newAmountItem;
};

const handleSellRequestTaskAssignee = (data, assignees) => {
  if (!data?.assignee) return null;

  const assignee = assignees?.find((user) => user?.value === data?.assignee);
  return {
    isActive: assignee?.isActive,
    text: assignee?.text || assignee?.email,
    value: assignee?.value,
  };
};

const handleSellRequestTaskChangelog = (changelog) => {
  if (!changelog) return [];

  return changelog.toReversed().map((item) => {
    const handledNewValue =
      item.fieldName === 'installments'
        ? item.newValue.map((newValue) =>
            newValue.map(handleAmountInstallmentsItem)
          )
        : item.newValue;

    return { ...item, newValue: handledNewValue };
  });
};

const handleSellRequestTaskRejectionReason = (customFields) => {
  const reason = REASONS_OF_REJECTION.find(
    (item) => item.value === customFields?.finalPriceRejectionReason
  );

  return reason
    ? {
        finalPriceRejectionReason: customFields?.finalPriceRejectionReason,
        finalPriceRejectionReasonOther:
          customFields?.finalPriceRejectionReasonOther,
      }
    : {
        finalPriceRejectionReason: 'Other',
        finalPriceRejectionReasonOther: customFields?.finalPriceRejectionReason,
      };
};

const handleSellRequestTaskComments = (comments) => {
  if (!comments) return [];

  return sortArray(comments, 'createdAt', 'desc').map((entry) => ({
    id: entry?.id,
    date: entry?.createdAt ? formatDate(entry?.createdAt, 'dd MMMM yyyy') : '-',
    time: entry?.createdAt ? formatDate(entry?.createdAt, 'HH:mm') : '-',
    name: entry?.userFullName || entry?.user || '-',
    comment: entry?.comment,
    edit: entry?.editedAt
      ? formatDate(entry?.editedAt, 'dd MMMM yyyy, HH:mm')
      : '',
  }));
};

const handleSellRequestTaskDocuments = (documents) => {
  if (!documents) return [];

  return documents.map((document) => ({
    ...document,
    cannotDelete: document?.type === 'BuyoutOffer',
  }));
};

const getValue = (data, current) => {
  return data.find((item) => item?.value === current);
};

export const normalizeSellRequestsTaskData = (
  data,
  assignees = [],
  stages = []
) => {
  if (!data) return {};

  const assignee = handleSellRequestTaskAssignee(data, assignees);
  const changelog = handleSellRequestTaskChangelog(data?.changelog);
  const rejectionReason = handleSellRequestTaskRejectionReason(
    data?.customFields
  );
  const comments = handleSellRequestTaskComments(data?.comments);
  const documents = handleSellRequestTaskDocuments(data?.documents);
  const status = getValue(stages, data.status);

  const customFields = {
    ...data?.customFields,
    approvalStatus: data?.customFields?.approvalStatus || 'Pending',
    finalPrice: data?.customFields?.finalPrice?.number,
    transferOfOwnershipFee: data?.customFields?.transferOfOwnershipFee?.number,
    offerSentDate: data?.customFields?.offerSentDate
      ? formatDate(data?.customFields?.offerSentDate, 'dd-MM-yyyy')
      : '',
    offerExpirationDate: data?.customFields?.offerExpirationDate
      ? formatDate(data?.customFields?.offerExpirationDate, 'dd-MM-yyyy')
      : '',
    nbv: data?.customFields?.nbv?.number,
    vehiclePurchasePrice: data?.customFields?.vehiclePurchasePrice?.number,
    extraCap: data?.customFields?.extraCap?.number,
    minimumPrice: data?.customFields?.minimumPrice?.number,
    commercialAssessment: data?.customFields?.commercialAssessment?.number,
    comparablePrice: data?.customFields?.comparablePrice?.number,
    loanRequestStatus: data?.customFields?.loanRequestStatus || 'Pending',
    administrativeExpenses: data?.customFields?.administrativeExpenses?.number,
    downpayment: data?.customFields?.downpayment?.number,
    financedAmount: data?.customFields?.financedAmount?.number,
    offerRequestDate: data?.customFields?.offerRequestDate
      ? formatDate(data?.customFields?.offerRequestDate, 'dd-MM-yyyy')
      : '',
    ...rejectionReason,
  };

  return {
    ...data,
    assignee,
    status,
    customFields,
    changelog,
    comments,
    documents,
  };
};

export const getSellRequestsQueryParamsAndPayload = ({ params, filters }) => {
  const cleanParams = getSellRequestsQueryParams({
    ...params,
  });

  const payload = getSellRequestsPayload({
    ...cleanParams,
    filters: filters.modalFiltersRules,
    boardId: SELL_REQUESTS_BOARD.id,
  });

  const exportPayload = omit(payload, ['boardType']);
  return { cleanParams, payloadSellRequests: payload, exportPayload };
};
